import { useState, useEffect } from 'react';
import type { Word } from '../../types';
import ApproveButton from '../ApproveButton';
import './StageCommon.css';

/**
 * Stage 5: Audio — ElevenLabs pronunciation generation.
 * Play, approve, or regenerate.
 */

interface AudioStageProps {
  word: Word;
  onGenerate: () => void;
  onApprove: (fields: Record<string, unknown>) => void;
  generating: boolean;
  saving: boolean;
  registerApprovePayload: (fn: (() => Record<string, unknown> | null) | null) => void;
}

export default function AudioStage({ word, onGenerate, onApprove, generating, saving, registerApprovePayload }: AudioStageProps) {
  const [hasPlayed, setHasPlayed] = useState<boolean>(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  useEffect(() => {
    if (!registerApprovePayload) return;
    registerApprovePayload(() => (hasPlayed && word.audio_url ? { audio_url: word.audio_url } : null));
  }, [registerApprovePayload, hasPlayed, word.audio_url]);

  // A fresh audio_url means a new clip — clear any prior load error.
  useEffect(() => { setAudioError(null); }, [word.audio_url]);

  return (
    <div className="stage-view">
      <div className="audio-stage">
        {!word.audio_url && (
          <div className="stage-generate-area">
            <button className="generate-btn" onClick={onGenerate} disabled={generating} title="Generate Audio (\u2318G)">
              {generating ? 'Generating Audio...' : 'Generate Audio'}
            </button>
          </div>
        )}

        {word.audio_url && (
          <div className="audio-review">
            <div className="audio-context">
              <div className="audio-context-field">
                <span className="audio-label">Word</span>
                <span className="audio-value content-text">{word.word}</span>
              </div>
              {word.phonetics && (
                <div className="audio-context-field">
                  <span className="audio-label">Phonetics</span>
                  <span className="audio-value">{word.phonetics}</span>
                </div>
              )}
              {word.pronunciation_tip && (
                <div className="audio-context-field">
                  <span className="audio-label">Pronunciation Tip</span>
                  <span className="audio-value">{word.pronunciation_tip}</span>
                </div>
              )}
            </div>

            <div className="audio-player-wrapper">
              <audio
                src={word.audio_url}
                controls
                className="audio-player"
                onPlay={() => setHasPlayed(true)}
                onError={() => setAudioError("This audio clip failed to load. Regenerate the audio, or check the URL.")}
              />
              {audioError && (
                <div
                  className="audio-error"
                  role="alert"
                  style={{ color: 'var(--danger, #c0392b)', fontSize: '0.85rem', marginTop: '8px' }}
                >
                  {audioError}
                </div>
              )}
            </div>

            <div className="audio-actions">
              <button className="regenerate-btn" onClick={onGenerate} disabled={generating}>
                {generating ? 'Regenerating...' : 'Regenerate Audio'}
              </button>
            </div>

            <ApproveButton
              onClick={() => onApprove({ audio_url: word.audio_url })}
              disabled={!hasPlayed || saving}
              loading={saving}
              label="Approve Audio"
            />
          </div>
        )}

        {generating && !word.audio_url && (
          <div className="stage-generating">
            <div className="generating-spinner" />
            <p>Generating audio...</p>
          </div>
        )}
      </div>
    </div>
  );
}
